import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { useState, useEffect, useRef } from 'react';
import useRefCallback from '@hzdg/use-ref-callback';
import { getScrollRect, useNearestScrollNodeRef, useScrollEffect } from './utils';
function intersects(bounds, rect) {
  var _bounds$top = bounds.top,
    top = _bounds$top === void 0 ? rect.top : _bounds$top,
    _bounds$right = bounds.right,
    right = _bounds$right === void 0 ? rect.width : _bounds$right,
    _bounds$bottom = bounds.bottom,
    bottom = _bounds$bottom === void 0 ? rect.height : _bounds$bottom,
    _bounds$left = bounds.left,
    left = _bounds$left === void 0 ? rect.left : _bounds$left;
  var inRangeVertical = typeof rect.top === 'number' && typeof top === 'number' && top <= rect.top && typeof bottom === 'number' && bottom >= rect.top;
  var inRangeHorizontal = typeof rect.left === 'number' && typeof left === 'number' && left <= rect.left && typeof right === 'number' && right >= rect.left;
  return inRangeVertical && inRangeHorizontal;
}
/**
 * `getIntersects` returns an `Intersects` value for
 * a given `Event` target and `ScrollIntersectionConfig`.
 *
 * `Intersects` describes whether or not any of the configured areas
 * currently intersect with the scroll position for the given `Event` target.
 *
 * If a single area has been configured in `ScrollIntersectionConfig`,
 * `Intersects` will be a boolean. If an array of areas has been configured,
 * this will be an array of booleans, where the index of each boolean
 * corresponds to the index of the area in the configuration array.
 */

export function getIntersects(event, config) {
  if (!config) return false;
  var target = event.currentTarget;
  if (target instanceof HTMLElement || target instanceof Document) {
    var rect = getScrollRect(target);
    if (Array.isArray(config)) {
      return config.map(function (c) {
        return intersects(c, rect);
      });
    } else {
      return intersects(config, rect);
    }
  }
  return false;
}
/**
 * `useScrollIntersection` is a React hook for components
 * that care about the interesction of the nearest scrollable container's
 * scroll position with one or more areas of the scrollable area.
 *
 * If a `providedRef` is passed to `useScrollIntersection`,
 * returns the `Intersects` value, consisting of one intersection
 * boolean or an array of booleans.
 *
 * If no `providedRef` is passed, returns an array containing an `Intersects`
 * value and a callback `ref`. The `ref` should be passed to an underlying
 * DOM node. Note that the node does not have to be scrollable itself,
 * as `useScrollIntersection` will traverse the DOM to find
 * a scrollable parent to observe.
 *
 * `Intersects` describes whether or not any of the configured areas
 * currently intersect with the nearest scrollable container's scroll position.
 *
 * If a single area has been configured, `Intersects` will be a boolean.
 * If an array of areas has been configured, this will be an array of booleans,
 * where the index of each boolean corresponds to the index of the area in
 * the configuration array.
 */

function useScrollIntersection(providedRefOrConfig, config) {
  var providedRef = undefined;
  if ("current" in providedRefOrConfig) {
    providedRef = providedRefOrConfig;
  } else {
    config = providedRefOrConfig;
  } // Keep track of changes to intersection config.

  var intersectionConfig = useRef(config);
  useEffect(function () {
    intersectionConfig.current = config;
  }, [config]); // Keep track of whether or not any configured areas
  // interesect the scroll position.

  var _useState = useState(function () {
      return Array.isArray(intersectionConfig.current) ? new Array(intersectionConfig.current.length).fill(false) : false;
    }),
    _useState2 = _slicedToArray(_useState, 2),
    intersects = _useState2[0],
    setIntersects = _useState2[1]; // Keep a ref to the nearest scrollable container.

  var _useRefCallback = useRefCallback(null),
    _useRefCallback2 = _slicedToArray(_useRefCallback, 2),
    ref = _useRefCallback2[0],
    setRef = _useRefCallback2[1];
  if (providedRef) setRef(providedRef.current);
  var scrollRef = useNearestScrollNodeRef(ref); // Subscribe to scroll events on the nearest scrolling element,
  // calling the handler whenever a scroll event occurs.

  useScrollEffect(scrollRef, function handleScrolChange(event) {
    setIntersects(getIntersects(event, intersectionConfig.current));
  }, []); // If a ref has been provided, just return the `Intersects` value.
  // If a ref has not not been provided, return a callback ref along with
  // the `Intersects` value.

  return providedRef ? intersects : [intersects, setRef];
}
export default useScrollIntersection;